// src/pages/WishlistPage.tsx
//
// The shopper's saved-for-later list (/wishlist). Entries live in
// localStorage (WishlistContext) as catalog item ids and are resolved against
// the catalog cache; items that have left the catalog are pruned from view.
// Hearts on the cards toggle items straight off the list.

import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import PageContainer, { GapSize } from "../components/PageContainer";
import PageSection from "../components/PageSection";
import ProductList from "../components/ProductList";
import { useCatalog } from "../contexts/CatalogContext";
import { useWishlist } from "../contexts/WishlistContext";
import { findOrGetItemsByCategory } from "../api";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { useShopLink } from "../hooks/useShopLink";

const WishlistPage = () => {
  const { wishlist } = useWishlist();
  const shopLink = useShopLink();
  const {
    getItemsByIds,
    getItemsByCategory,
    getLowestCategoryByItem,
    addItems,
    addImages,
    getImagesByIds,
  } = useCatalog();

  useDocumentMeta({
    title: "My Wishlist",
    description: "Products you've saved at The Americana Company.",
  });

  const wishlistIds = useMemo(
    () => wishlist.map((entry) => entry.productId),
    [wishlist],
  );
  const items = getItemsByIds(wishlistIds);

  // Ensure the images for saved items are in the catalog cache — they're
  // normally only fetched when the item's category page is visited.
  useEffect(() => {
    const categoryIds = new Set(
      items
        .map((item) => getLowestCategoryByItem(item.id ?? "")?.id)
        .filter((id): id is string => !!id),
    );
    categoryIds.forEach((categoryId) => {
      findOrGetItemsByCategory(
        categoryId,
        addItems,
        getItemsByCategory,
        addImages,
        getImagesByIds,
      ).catch((error) => console.error("Get wishlist items failed:", error));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wishlistIds]);

  return (
    <PageContainer gapSize={GapSize.Small}>
      <PageSection className="hero">
        <h1>My Wishlist</h1>
        <p>
          {items.length === 0
            ? "Everything you save is kept here, ready when you are."
            : `${items.length} saved ${
                items.length === 1 ? "piece" : "pieces"
              }, ready when you are.`}
        </p>
      </PageSection>

      <PageSection className="flex justify-center py-4">
        {items.length === 0 ? (
          <div className="empty-state">
            {/* Same heart as WishlistButton, so the empty state points at the
                control the shopper is being told to use. */}
            <div aria-hidden="true" className="empty-state-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-7 w-7"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </div>
            <h2 className="playfair-text text-xl text-brown-dark">
              Nothing saved yet
            </h2>
            <p className="lora-text text-sm text-gray-600 max-w-xs">
              Tap the heart on any product and it will be waiting for you here.
            </p>
            <Link to={shopLink} className="btn-primary mt-2">
              Browse the Shop
            </Link>
          </div>
        ) : (
          <div className="w-full">
            <ProductList items={items} />
          </div>
        )}
      </PageSection>
    </PageContainer>
  );
};

export default WishlistPage;
